import React, { useEffect, useState, Fragment } from 'react';
import {
  Button,
  Typography,
  TextField,
  InputAdornment,
  MenuItem,
} from '@material-ui/core';
import { format } from 'date-fns';
import { Formik } from 'formik';
import { toJS } from 'mobx';
import SimpleSelect from '../util/SimpleSelect';
import { useRootStore } from '../../stores/BridgeProvider';
import Spinner from '../util/Spinner';
import {
  ErrorText,
  ButtonFlex,
  StyledForm,
  FormRow,
  RowItem,
} from '../styles/styles';
import { MenuList, filterOptions } from '../util/ReactSelectOverrides';
import {
  safeEval,
  safeFalsy,
  formInitialPriceHandler,
} from '../util/UtilFunctions';
import { PaymentSchema } from '../../validation';
import { CustomCreatableSelect } from '../util/CustomCreatableSelect';
import AccountSelect from '../util/AccountSelect';
import ApplyToVoucher from './PaymentInfo/ApplyToVoucher';
import { useSnackBars } from '../../alerts/SnackBarProvider';
import { COLORS } from '../util/Colors';

const PaymentForm = ({ onSubmit, payment = {}, buttons }) => {
  const { authStore, dataStore, orgStore } = useRootStore();
  const [isLoading, setLoading] = useState(true);
  const [isCreating, setCreating] = useState(false);
  const [paymentTypes, setPaymentTypes] = useState([]);
  const [paymentStatuses, setPaymentStatuses] = useState([]);
  const [initialPaymentVouchers, setInitialPaymentVouchers] = useState([]);
  const handleAlert = useSnackBars();
  const { PaymentId, Amount } = payment;
  const { settings, isIntegrated } = orgStore;
  const { ExtSysId } = settings || {};
  const orgIntegrated = ExtSysId > 0;
  const contacts = dataStore.contactArray;
  const accounts = dataStore.validAccountArray;
  const vouchers = dataStore.voucherArray;
  const fetchContacts = async () => {
    if (contacts.length === 0) {
      await dataStore.retrieveContacts();
    }
  };
  const fetchAccounts = async () => {
    if (accounts.length === 0) {
      await dataStore.retrieveAccounts();
    }
  };
  const fetchVouchers = async () => {
    if (vouchers.length === 0) {
      await dataStore.retrieveAllVouchers();
    }
  };
  const fetchPaymentTypes = async () => {
    try {
      const { data } = await authStore.request.get(
        `${process.env.REACT_APP_API}/api/payment-types`,
      );
      setPaymentTypes(data);
    } catch (e) {
      console.error(e);
    }
  };
  const fetchPaymentStatuses = async () => {
    try {
      const { data } = await authStore.request.get(
        `${process.env.REACT_APP_API}/api/payment-statuses`,
      );
      setPaymentStatuses(data);
    } catch (e) {
      console.error(e);
    }
  };
  const fetchPaymentVouchers = async () => {
    if (PaymentId) {
      try {
        const { data } = await authStore.request.get(
          `${process.env.REACT_APP_API}/api/payment-voucher-payment/${PaymentId}`,
        );
        setInitialPaymentVouchers(data);
      } catch (e) {
        console.error(e);
      }
    }
  };
  useEffect(() => {
    const initialActions = async () => {
      await Promise.all([
        fetchPaymentTypes(),
        fetchPaymentStatuses(),
        fetchContacts(),
        fetchAccounts(),
        fetchVouchers(),
        fetchPaymentVouchers(),
      ]);
      setLoading(false);
    };
    initialActions();
  }, []);
  if (isLoading || !payment) return <Spinner />;

  const vtype = type => {
    const receivable = [
      'Receivable',
      'Receivable Credit',
      'Direct Payment Received',
    ];
    const payables = ['Payable', 'Payable Credit', 'Direct Payment Sent'];

    if (receivable.includes(type)) {
      return 'AR';
    }
    if (payables.includes(type)) return 'AP';
  };

  const filteredPaymentTypes = payment.VoucherTypeDesc
    ? paymentTypes.filter(pt => pt.TranType === vtype(payment.VoucherTypeDesc))
    : paymentTypes;

  return (
    <Formik
      validationSchema={PaymentSchema}
      initialValues={{
        PaymentId: safeFalsy(payment.PaymentId),
        Contact: dataStore.getContactFromId(payment.ContactId),
        ContactId: safeFalsy(payment.ContactId),
        PaymentDate: payment.PaymentDate
          ? format(payment.PaymentDate, 'YYYY-MM-DD')
          : format(new Date(), 'YYYY-MM-DD'),
        DepositDate: payment.DepositDate
          ? format(payment.DepositDate, 'YYYY-MM-DD')
          : '',
        ClearedDate: payment.ClearedDate
          ? format(payment.ClearedDate, 'YYYY-MM-DD')
          : '',
        // Amount: safeFalsy(payment.Amount),
        Amount: formInitialPriceHandler(payment.Amount),
        Description: safeFalsy(payment.Description),
        StatusId: safeFalsy(payment.StatusId),
        Account: dataStore.getAccountFromId(payment.AccountId),
        AccountId: safeFalsy(payment.AccountId),
        TypeId:
          payment.VoucherTypeDesc &&
          filteredPaymentTypes &&
          filteredPaymentTypes.length > 0
            ? filteredPaymentTypes[0].PaymentTypeId
            : safeFalsy(payment.PaymentTypeId),
        TranType: payment.VoucherTypeDesc
          ? safeEval(() => vtype(payment.VoucherTypeDesc), '')
          : safeFalsy(payment.TranType),
        IsCleared: safeFalsy(payment.IsCleared, 0),
        // FIXME: This stuff
        ApprovedBy: safeFalsy(payment.ApprovedBy),
        IsApplied: safeFalsy(payment.IsApplied),
        PaymentVouchers: initialPaymentVouchers,
      }}
      onSubmit={async (values, actions) => {
        if (orgIntegrated && values.PaymentVouchers.length === 0) {
          handleAlert('Payment must be applied to a voucher', COLORS.red);
          actions.setSubmitting(false);
          return;
        }

        await onSubmit(values);
        actions.setSubmitting(false);
      }}
      render={({
        handleBlur,
        handleChange,
        setFieldValue,
        errors,
        handleSubmit,
        values,
        isSubmitting,
        touched,
        dirty,
      }) => {
        const showError = val => errors[val] && touched[val];
        const handleCreateContact = async inputValue => {
          setCreating(true);
          try {
            const { data: contactData } = await authStore.request.post(
              `${process.env.REACT_APP_API}/api/contact`,
              {
                Name: inputValue,
                IsAgent: 0,
              },
            );
            const { id: cId } = contactData;
            await dataStore.retrieveContacts();
            const contact = dataStore.getContactFromId(cId);
            setFieldValue('Contact', contact);
            setFieldValue('ContactId', cId);
          } catch (e) {
            console.error(e);
          }
          setCreating(false);
        };
        const isValidAccountGroup = TypeId => {
          const { TranType } = values;
          if (TranType === 'AP') {
            return [2, 3, 4, 5, 6, 7].includes(TypeId);
          }
          if (TranType === 'AR') {
            return [1, 3, 4, 5, 6, 7].includes(TypeId);
          }
          return true;
        };

        const moveAccountTypeToFront = (list, typeId) => {
          const index = list.findIndex(({ id }) => id === typeId);
          list.splice(0, 0, list.splice(index, 1)[0]);
          return list;
        };

        const changePaymentVouchers = newPVs =>
          setFieldValue('PaymentVouchers', newPVs);

        const label = (() => {
          if (isIntegrated) return 'Payment Info';
          return payment.PaymentId ? 'Edit Payment' : 'New Payment';
        })();

        return (
          <div>
            <Typography variant="h2" gutterBottom>
              {label}
            </Typography>
            <StyledForm onSubmit={handleSubmit}>
              <div style={{ width: '100%', zIndex: 1001 }}>
                <CustomCreatableSelect
                  isSearchable
                  isClearable
                  placeholder="Contact"
                  isRequired
                  error={showError('ContactId')}
                  components={{ MenuList }}
                  options={contacts}
                  getOptionLabel={option => option.Name}
                  getOptionValue={option => option.ContactId}
                  isValidNewOption={(inputValue, x, selectOptions) =>
                    selectOptions.findIndex(
                      option => option.Name === inputValue,
                    ) < 0
                  }
                  formatCreateLabel={inputValue => `Create "${inputValue}"`}
                  getNewOptionData={(inputValue, optionLabel) => ({
                    Name: optionLabel,
                    ContactId: inputValue,
                    __isNew__: true,
                  })}
                  value={values.Contact}
                  filterOption={filterOptions}
                  isDisabled={isCreating || isIntegrated}
                  isLoading={isCreating}
                  onCreateOption={handleCreateContact}
                  onChange={val => {
                    setFieldValue('Contact', val);
                    setFieldValue(
                      'ContactId',
                      safeEval(() => val.ContactId),
                      null,
                    );
                  }}
                />
              </div>
              {showError('ContactId') && (
                <ErrorText>{errors.ContactId}</ErrorText>
              )}
              <FormRow>
                <RowItem>
                  <SimpleSelect
                    label="Transaction Type"
                    isRequired
                    selectProps={{
                      disabled:
                        isIntegrated ||
                        values.PaymentId !== '' ||
                        !!payment.VoucherTypeDesc,
                    }}
                    handleChange={handleChange}
                    value={values.TranType}
                    name="TranType"
                    containerStyle={{ width: '100%' }}
                    error={showError('TranType')}
                  >
                    <MenuItem value="AP">AP</MenuItem>
                    <MenuItem value="AR">AR</MenuItem>
                  </SimpleSelect>
                  {showError('TranType') && (
                    <ErrorText>{errors.TranType}</ErrorText>
                  )}
                </RowItem>
                <RowItem>
                  <div style={{ width: '100%', zIndex: 1000 }}>
                    <AccountSelect
                      placeholder="Account"
                      isRequired
                      error={showError('AccountId')}
                      value={values.Account}
                      changeMemoVals={values.TranType}
                      isValidAccountGroup={isValidAccountGroup}
                      isDisabled={isIntegrated}
                      sortFunction={list => {
                        if (values.TranType === 'AP') {
                          return moveAccountTypeToFront(
                            moveAccountTypeToFront(list, 7),
                            2,
                          );
                        }
                        if (values.TranType === 'AR') {
                          return moveAccountTypeToFront(
                            moveAccountTypeToFront(list, 6),
                            1,
                          );
                        }
                        return list;
                      }}
                      onChange={val => {
                        setFieldValue('Account', val);
                        setFieldValue(
                          'AccountId',
                          safeEval(() => val.AccountId),
                          null,
                        );
                      }}
                    />
                  </div>
                  {showError('AccountId') && (
                    <ErrorText>{errors.AccountId}</ErrorText>
                  )}
                </RowItem>
              </FormRow>
              <FormRow>
                <RowItem>
                  <SimpleSelect
                    selectProps={{
                      disabled:
                        isIntegrated ||
                        values.TranType === '' ||
                        values.PaymentId !== '',
                    }}
                    label="Payment Type"
                    error={showError('TypeId')}
                    isRequired
                    handleChange={handleChange}
                    value={values.TypeId}
                    name="TypeId"
                    containerStyle={{ width: '100%' }}
                  >
                    {filteredPaymentTypes.map(
                      ({ PaymentTypeId, PaymentTypeName, TranType }) => {
                        if (TranType === values.TranType)
                          return (
                            <MenuItem key={PaymentTypeId} value={PaymentTypeId}>
                              {PaymentTypeName}
                            </MenuItem>
                          );
                      },
                    )}
                  </SimpleSelect>
                  {showError('TypeId') && (
                    <ErrorText>{errors.TypeId}</ErrorText>
                  )}
                </RowItem>
                <RowItem>
                  <SimpleSelect
                    label="Payment Status"
                    error={showError('StatusId')}
                    isRequired
                    handleChange={handleChange}
                    value={values.StatusId}
                    name="StatusId"
                    containerStyle={{ width: '100%' }}
                    selectProps={{
                      disabled: isIntegrated,
                    }}
                  >
                    {paymentStatuses.map(
                      ({ PaymentStatusId, PaymentStatusName }) => (
                        <MenuItem key={PaymentStatusId} value={PaymentStatusId}>
                          {PaymentStatusName}
                        </MenuItem>
                      ),
                    )}
                  </SimpleSelect>

                  {showError('StatusId') && (
                    <ErrorText>{errors.StatusId}</ErrorText>
                  )}
                </RowItem>
              </FormRow>
              <FormRow>
                <RowItem>
                  <TextField
                    name="Amount"
                    label={
                      <div>
                        Amount <span style={{ color: 'red' }}>*</span>
                      </div>
                    }
                    value={values.Amount}
                    margin="normal"
                    disabled={isIntegrated}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    variant="outlined"
                    fullWidth
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">$</InputAdornment>
                      ),
                    }}
                  />
                  {showError('Amount') && (
                    <ErrorText>{errors.Amount}</ErrorText>
                  )}
                </RowItem>
                <RowItem>
                  <TextField
                    placeholder="mm/dd/yyyy"
                    name="PaymentDate"
                    label={
                      <div>
                        Payment Date <span style={{ color: 'red' }}>*</span>
                      </div>
                    }
                    type="date"
                    value={values.PaymentDate}
                    margin="normal"
                    onChange={handleChange}
                    onBlur={handleBlur}
                    disabled={isIntegrated}
                    variant="outlined"
                    fullWidth
                    InputLabelProps={{
                      shrink: true,
                    }}
                  />
                  {showError('PaymentDate') && (
                    <ErrorText>{errors.PaymentDate}</ErrorText>
                  )}
                </RowItem>
              </FormRow>
              <FormRow>
                <RowItem>
                  <TextField
                    placeholder="mm/dd/yyyy"
                    name="DepositDate"
                    label={
                      <div>
                        Deposit Date <span style={{ color: 'red' }}>*</span>
                      </div>
                    }
                    type="date"
                    value={values.DepositDate}
                    margin="normal"
                    onChange={handleChange}
                    onBlur={handleBlur}
                    disabled={isIntegrated}
                    variant="outlined"
                    fullWidth
                    InputLabelProps={{
                      shrink: true,
                    }}
                  />
                  {showError('DepositDate') && (
                    <ErrorText>{errors.DepositDate}</ErrorText>
                  )}
                </RowItem>
                <RowItem>
                  <TextField
                    placeholder="mm/dd/yyyy"
                    name="ClearedDate"
                    label={
                      <div>
                        Cleared Date <span style={{ color: 'red' }}>*</span>
                      </div>
                    }
                    type="date"
                    value={values.ClearedDate}
                    margin="normal"
                    onChange={handleChange}
                    onBlur={handleBlur}
                    disabled={isIntegrated}
                    variant="outlined"
                    fullWidth
                    InputLabelProps={{
                      shrink: true,
                    }}
                  />
                  {showError('ClearedDate') && (
                    <ErrorText>{errors.ClearedDate}</ErrorText>
                  )}
                </RowItem>
              </FormRow>

              <TextField
                name="Description"
                label="Description"
                margin="normal"
                value={values.Description}
                onChange={handleChange}
                onBlur={handleBlur}
                disabled={isIntegrated}
                variant="outlined"
                fullWidth
                multiline
                error={showError('Description')}
              />
              <ApplyToVoucher
                payment={payment}
                paymentVouchers={values.PaymentVouchers}
                fetchPaymentVouchers={fetchPaymentVouchers}
                changePaymentVouchers={changePaymentVouchers}
              />
              {!isIntegrated && (
                <ButtonFlex>
                  <Button
                    color="primary"
                    variant="contained"
                    type="submit"
                    size="large"
                    disabled={!dirty || isSubmitting}
                  >
                    Submit
                  </Button>
                  {buttons &&
                    buttons.map((button, index) => (
                      <Fragment key={index}>{button}</Fragment>
                    ))}
                </ButtonFlex>
              )}
            </StyledForm>
          </div>
        );
      }}
    />
  );
};

export default PaymentForm;
